/** @jsxImportSource @emotion/react */
import { SuitabilityBadge } from '@app/domains/suitability/components/SuitabilityBadge'
import { SuitabilityGrade } from '@app/domains/suitability/types'
import { Box, Link, Typography } from '@mui/material'
import { ColumnDef } from '@tanstack/react-table'
import { Link as RouterLink } from 'react-router-dom'
import { HazardTableRow } from '../types/hazard.types'
import { modelNameCell, modelNameSubtitle, modelNameText } from './HazardTable.styles'
import { isValidSuitabilityGrade } from '@app/domains/suitability/utils/suitability.utils'

export const getStaticColumns = (): ColumnDef<HazardTableRow>[] => [
  {
    id: 'name',
    accessorKey: 'name',
    header: 'Hazard Model',
    enableHiding: false,
    size: 280,
    sortingFn: 'alphanumeric',
    cell: ({ row }) => (
      <Box css={modelNameCell}>
        <Typography
          component={RouterLink}
          to={`/app/home/data/detail/${row.original.type}/overview`}
          css={modelNameText}
          sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
        >
          {row.original.category}
        </Typography>

        <Typography
          component={RouterLink}
          to={`/app/home/data/detail/${row.original.type}/overview`}
          variant="caption"
          css={modelNameSubtitle}
          display="block"
        >
          {row.original.name}
        </Typography>
      </Box>
    ),
  },
  {
    accessorKey: 'group',
    header: 'Group',
    size: 160,
    cell: (info) => (
      <Typography variant="caption" color="text.secondary">
        {info.getValue<string | null>() ?? '—'}
      </Typography>
    ),
  },
  {
    accessorKey: 'confidenceRating',
    header: 'Confidence',
    size: 130,
    cell: (info) => {
      const value = info.getValue<string | null>()
      if (!value)
        return (
          <Typography variant="caption" color="text.disabled">
            —
          </Typography>
        )

      if (!isValidSuitabilityGrade(value)) {
        return (
          <Typography variant="caption" color="text.secondary">
            {value}
          </Typography>
        )
      }

      return <SuitabilityBadge letterGrade={value as SuitabilityGrade} size="sm" label={value} />
    },
  },
  {
    accessorKey: 'createdAt',
    header: 'Date Modelled',
    size: 150,
    cell: (info) => (
      <Typography variant="caption" color="text.secondary">
        {info.getValue<string | null>() ?? '—'}
      </Typography>
    ),
  },
  {
    accessorKey: 'owner',
    header: 'Contact',
    size: 150,
    cell: (info) => {
      const { ownerName, ownerOrganization, ownerEmail } = info.row.original
      const parts = [ownerName, ownerOrganization, ownerEmail].filter(Boolean)

      {
        /* fall back to owner field if detailed contact details are missing */
      }
      if (parts.length === 0) {
        return (
          <Typography variant="caption" color="text.disabled">
            {info.getValue<string | null>() ?? '—'}
          </Typography>
        )
      }

      return ownerEmail ? (
        <Typography variant="caption" color="text.secondary">
          <Link href={`mailto:${ownerEmail}`} underline="hover">
            {ownerName || ownerOrganization || ownerEmail}
            <br />
            {ownerName || ownerEmail ? ownerOrganization : null}
          </Link>
        </Typography>
      ) : (
        <Typography variant="caption" color="text.secondary">
          {ownerName || ownerOrganization || ownerEmail}
          <br />
          {ownerName || ownerEmail ? ownerOrganization : null}
        </Typography>
      )
    },
  },
  {
    accessorKey: 'publicInfo',
    header: 'Public Info',
    size: 140,
    cell: ({ row }) => {
      const links = row.original.sourceLinks
      if (!links?.length)
        return (
          <Typography variant="caption" color="text.disabled">
            —
          </Typography>
        )
      return (
        <Link
          href={links[0].url}
          target="_blank"
          rel="noopener noreferrer"
          variant="caption"
          underline="hover"
        >
          Online viewer
        </Link>
      )
    },
  },
  {
    accessorKey: 'limitationsCaveats',
    header: 'Important Notes',
    size: 200,
    cell: (info) => {
      const value = info.getValue<string | null>()
      if (!value)
        return (
          <Typography variant="caption" color="text.disabled">
            —
          </Typography>
        )

      const plain = value.replace(/\n+/g, ' ').trim()

      return (
        <Typography variant="caption" color="text.secondary">
          {plain}
        </Typography>
      )
    },
  },
  {
    accessorKey: 'license',
    header: 'License',
    size: 160,
    cell: (info) => {
      const license = info.getValue<{ title: string; url?: string } | null>()
      if (!license)
        return (
          <Typography variant="caption" color="text.disabled">
            —
          </Typography>
        )

      return license.url ? (
        <Link
          href={license.url}
          target="_blank"
          rel="noopener noreferrer"
          variant="caption"
          underline="hover"
        >
          {license.title}
        </Link>
      ) : (
        <Typography variant="caption" color="text.secondary">
          {license.title}
        </Typography>
      )
    },
  },
  {
    accessorKey: 'spatialExtent',
    header: 'Spatial Extent',
    size: 160,
    cell: (info) => (
      <Typography variant="caption" color="text.secondary">
        {info.getValue<string | null>() ?? '—'}
      </Typography>
    ),
  },
  {
    accessorKey: 'provider',
    header: 'Provider',
    size: 160,
    cell: (info) => (
      <Typography variant="caption" color="text.secondary">
        {info.getValue<string | null>() ?? '—'}
      </Typography>
    ),
  },
]

export const getSuitabilityColumns = (
  useCases: { slug: string; name: string }[],
): ColumnDef<HazardTableRow>[] =>
  useCases.map(({ slug, name }) => ({
    id: `suitability_${slug}`,
    header: name,
    size: 180,
    cell: ({ row }) => {
      const record = row.original.suitability.find((s) => s.use_case_slug === slug) ?? null

      if (!record) {
        return (
          <Typography variant="caption" color="text.disabled">
            —
          </Typography>
        )
      }

      return (
        <SuitabilityBadge letterGrade={record.suitability_grade as SuitabilityGrade} size="sm" />
      )
    },
  }))

export const getHiddenColumns = (): ColumnDef<HazardTableRow>[] => [
  {
    accessorKey: 'category',
    header: '',
    enableHiding: true,
    enableGlobalFilter: true,
    filterFn: 'equals',
    cell: () => null,
    meta: { hidden: true },
  },
]
